import classNames from 'classnames';
import _ from 'lodash';
import Quill from 'quill/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce, useThrottledCallback } from 'use-debounce';
import useAppSelector from '../app/redux/useAppSelector';
import closestLine from '../editor/closestLine';
import { useQuill } from '../editor/QuillContext';
import useEventListener from '../utils/useEventListener';
import PostActionButtons from './actionButtons/PostActionButtons';
import PostTOCSticky from './actionButtons/PostTOCSticky';
import {
  getScrollContainer,
  useListenToPostScroll,
} from './listenToPostScroll';
import styles from './PostStickyControl.module.scss';

const ACTIONS_TOP_OFFSET = parseInt(styles.postActionsTopOffset, 10);
const HEADER_CONTROL_HEIGHT = 32;

const tallerThanEditor = (quill: Quill, offset: number) => {
  let lastVisibleElementChild = quill.root.lastElementChild;
  while (lastVisibleElementChild instanceof HTMLElement) {
    if (lastVisibleElementChild.offsetParent) {
      return (
        offset >
        lastVisibleElementChild.offsetTop + lastVisibleElementChild.offsetHeight
      );
    }

    lastVisibleElementChild = lastVisibleElementChild.previousElementSibling;
  }

  return false;
};

const useHoveredHeaderOffset = () => {
  const { quill } = useQuill();
  const [offset, setOffset] = useState<number | null>(null);
  const [debouncedOffset, { flush }] = useDebounce(offset, 500);

  useEventListener(quill?.root, 'mouseover', (e) => {
    const blot =
      e.target instanceof Element &&
      closestLine(quill?.scroll.find(e.target, true));
    if (
      blot &&
      blot.domNode instanceof HTMLElement &&
      blot.statics.blotName === 'header'
    ) {
      setOffset(blot.domNode.offsetTop);
    } else {
      setOffset(null);
    }
  });

  useEventListener(quill?.root, 'mouseleave', () => {
    setOffset(null);
    // Update immediately to avoid TOC button repositioning
    // after user moves the cursor over the TOC button.
    flush();
  });

  return debouncedOffset;
};

const usePostLayoutBannerRef = () => {
  const postLayoutBannerRef = useRef<HTMLElement | null>(null);
  const currentPostBanner = useAppSelector(
    (state) => state.editor.currentPostBanner,
  );

  useEffect(() => {
    postLayoutBannerRef.current = document.querySelector('.post-layout-banner');
  }, [currentPostBanner]);

  return postLayoutBannerRef;
};

const useElementHeightObserver = (
  element: HTMLElement | null | undefined,
  setHeight: (height: number) => void,
) => {
  useEffect(() => {
    setHeight(element?.offsetHeight ?? 0);
    if (!element) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      const { height } = entries[0]?.contentRect || {};
      if (typeof height === 'number') {
        setHeight(height);
      }
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [element, setHeight]);
};

const useWiderLineRanges = () => {
  const VERTICAL_MARGIN = 8;
  const { quill } = useQuill();
  const [ranges, setRanges] = useState<{ top: number; bottom: number }[]>([]);
  const { callback: throttledUpdateRange, flush } = useThrottledCallback(
    () => {
      const newRanges = quill
        ? Array.from(quill.container.querySelectorAll('.span-full')).flatMap(
            (node) => {
              if (!(node instanceof HTMLElement) || !node.offsetParent) {
                return [];
              }
              const top = node.offsetTop;
              return [
                {
                  top: top - VERTICAL_MARGIN,
                  bottom: top + node.offsetHeight + VERTICAL_MARGIN,
                },
              ];
            },
          )
        : [];

      if (!_.isEqual(ranges, newRanges)) {
        setRanges(newRanges);
      }
    },
    200,
    { leading: false },
  );

  const forceUpdateRange = useCallback(() => {
    throttledUpdateRange();
    flush();
  }, [throttledUpdateRange, flush]);

  // Images maybe resized after loaded.
  useElementHeightObserver(quill?.root, forceUpdateRange);

  useEffect(() => {
    if (!quill) return undefined;
    quill.on(Quill.events.TEXT_CHANGE, throttledUpdateRange);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, throttledUpdateRange);
    };
  }, [quill, throttledUpdateRange]);

  return ranges;
};

interface StyleModifier {
  static?: boolean;
  top?: number;
  transition?: boolean;
  // CSS transition doesn't support transit from sticky position to static position.
  // In order to achieve this, we need to update the position to the target one and
  // keep the top offset visually the same. And then change the top offset to the
  // target one.
  nextStyles?: Omit<StyleModifier, 'nextStyles'>;
}

interface PostStickyControlProps {
  postId: string;
  tocClassName: string;
  actionButtonsClassName: string;
  isStickyTOCOpen?: boolean;
  onToggleStickyTOC: () => void;
}

const PostStickyControl = ({
  postId,
  tocClassName,
  actionButtonsClassName,
  isStickyTOCOpen = false,
  onToggleStickyTOC,
}: PostStickyControlProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { quill } = useQuill();
  const [tocElement, setTOCElement] = useState<HTMLElement | null>(null);
  const [actionsElement, setActionsElement] = useState<HTMLElement | null>(
    null,
  );
  const [tocHeight, setTOCHeight] = useState(0);
  const [actionsHeight, setActionsHeight] = useState(0);
  const [styleModifier, setStyleModifier] = useState<StyleModifier>({});

  const widerLineRanges = useWiderLineRanges();
  const headerOffset = useHoveredHeaderOffset();
  const postLayoutBanner = usePostLayoutBannerRef();

  useElementHeightObserver(tocElement, setTOCHeight);
  useElementHeightObserver(actionsElement, setActionsHeight);

  const { callback: throttledUpdateStaticTop, flush } = useThrottledCallback(
    () => {
      if (styleModifier.nextStyles) return;
      const height = Math.max(actionsHeight, tocHeight);
      if (!quill || !ref.current || height === 0) {
        setStyleModifier({});
        return;
      }

      const bannerTop = postLayoutBanner.current?.getBoundingClientRect().top;
      const quillTop = quill.root.getBoundingClientRect().top;
      const editorTopOffset = bannerTop ? quillTop - bannerTop : 0;

      const adjustedWiderLineRanges = widerLineRanges.map(
        ({ top, bottom }) => ({
          top: top + editorTopOffset,
          bottom: bottom + editorTopOffset,
        }),
      );

      const ranges: { top: number; bottom: number; isHeader?: boolean }[] =
        headerOffset
          ? _.sortBy(
              [
                ...adjustedWiderLineRanges,
                {
                  top: headerOffset + editorTopOffset,
                  bottom:
                    headerOffset + editorTopOffset + HEADER_CONTROL_HEIGHT,
                  isHeader: true,
                },
              ],
              ['top'],
            )
          : adjustedWiderLineRanges;

      const editorTopRelativeToScrollContainer =
        (bannerTop ?? quillTop) -
        (getScrollContainer()?.getBoundingClientRect().top ?? 0);

      const stickyTopRelativeToEditor = Math.max(
        parseInt(
          getComputedStyle(ref.current).getPropertyValue(
            '--post-sticky-control-top',
          ),
          10,
        ) - editorTopRelativeToScrollContainer,
        0,
      );

      if (
        ranges.every(
          (range) =>
            stickyTopRelativeToEditor - ACTIONS_TOP_OFFSET >= range.bottom ||
            stickyTopRelativeToEditor +
              (range.isHeader ? actionsHeight : height) +
              ACTIONS_TOP_OFFSET <=
              range.top,
        ) &&
        // Sticky elements don't occupy space
        !tallerThanEditor(quill, stickyTopRelativeToEditor + height)
      ) {
        if (styleModifier.top) {
          setStyleModifier({
            top: styleModifier.top - stickyTopRelativeToEditor,
            nextStyles: { transition: true },
          });
        }
        return;
      }

      const rangesAfter = ranges.filter(
        ({ bottom }) =>
          bottom + ACTIONS_TOP_OFFSET >= stickyTopRelativeToEditor,
      );
      _.forEach(rangesAfter, ({ bottom }, index) => {
        const top = bottom + ACTIONS_TOP_OFFSET;
        if (
          rangesAfter
            .slice(index + 1)
            .every(
              (nextRange) =>
                top +
                  (nextRange.isHeader ? actionsHeight : height) +
                  ACTIONS_TOP_OFFSET <=
                nextRange.top,
            )
        ) {
          const newStyle = {
            static: true,
            top: bottom,
            transition: true,
          } as const;
          if (!styleModifier.static) {
            setStyleModifier({
              static: true,
              top: stickyTopRelativeToEditor,
              nextStyles: newStyle,
            });
          } else if (styleModifier.top !== newStyle.top) {
            setStyleModifier(newStyle);
          }
          return false;
        }

        return true;
      });
    },
    100,
    { leading: false },
  );

  useListenToPostScroll(throttledUpdateStaticTop);

  useEffect(() => {
    throttledUpdateStaticTop();
    flush();
  }, [
    flush,
    throttledUpdateStaticTop,
    tocHeight,
    actionsHeight,
    widerLineRanges,
    headerOffset,
  ]);

  const { nextStyles } = styleModifier;
  useEffect(() => {
    if (nextStyles && ref.current) {
      // Force a repaint to ensure the current styleModifier is applied.
      // This is inspired by https://github.com/reactjs/react-transition-group/blob/740b1dd/src/CSSTransition.js#L194-L199
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      ref.current.scrollTop;
      setStyleModifier(nextStyles);
    }
  }, [nextStyles]);

  return (
    <div
      ref={ref}
      style={{
        // @ts-expect-error
        '--post-sticky-control-position': styleModifier.static
          ? 'static'
          : undefined,
        '--post-sticky-control-transition': styleModifier.transition
          ? 'all'
          : undefined,
        '--post-sticky-control-offset':
          typeof styleModifier.top === 'number'
            ? `${styleModifier.top}px`
            : undefined,
      }}
      className={styles.container}
    >
      <div className={classNames(styles.toc, tocClassName)}>
        <div ref={setTOCElement}>
          <PostTOCSticky isOpen={isStickyTOCOpen} postId={postId} />
        </div>
      </div>
      <div className={classNames(styles.actionButtons, actionButtonsClassName)}>
        <div ref={setActionsElement}>
          <PostActionButtons
            postId={postId}
            isStickyTOCOpen={isStickyTOCOpen}
            onToggleStickyTOC={onToggleStickyTOC}
          />
        </div>
      </div>
    </div>
  );
};

export default PostStickyControl;
